import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchMasterNames, createMasterName, deleteMasterName } from "../service/masterNameService";
import type { AcademicMasterName, CreateAcademicMasterNamePayload } from "../types";

export const loadMasterNames = createAsyncThunk("academicMasterNames/loadMasterNames", async () => {
  // Loaded once, all types — the list is small and templates need every type's
  // names available as soon as a node's type is picked.
  return fetchMasterNames();
});

export const addMasterName = createAsyncThunk(
  "academicMasterNames/addMasterName",
  async (payload: CreateAcademicMasterNamePayload) => createMasterName(payload)
);

export const removeMasterName = createAsyncThunk(
  "academicMasterNames/removeMasterName",
  async (id: number) => {
    await deleteMasterName(id);
    return id;
  }
);

interface MasterNameState {
  masterNames: AcademicMasterName[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

const initialState: MasterNameState = {
  masterNames: [],
  loading: false,
  loaded: false,
  error: null,
};

const masterNameSlice = createSlice({
  name: "academicMasterNames",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadMasterNames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMasterNames.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.masterNames = action.payload;
      })
      .addCase(loadMasterNames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load master names";
      })
      .addCase(addMasterName.fulfilled, (state, action) => {
        state.masterNames.push(action.payload);
      })
      .addCase(addMasterName.rejected, (state, action) => {
        state.error = action.error.message ?? "Failed to add master name";
      })
      .addCase(removeMasterName.fulfilled, (state, action) => {
        state.masterNames = state.masterNames.filter((m) => m.id !== action.payload);
      });
  },
});

export default masterNameSlice.reducer;
